'use client';

import { startTransition, useState, useMemo } from 'react';
import { useAbTesting } from '@packages/shared/src/abtesting/useAbTesting/useAbTesting';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { useSession } from '@packages/shared/src/hooks/useSession/useSession';
import { getBridge } from '@packages/shared/src/utils/getBridge';
import { useAtomValue } from 'jotai';
import { type MessageDescriptor, useIntl } from 'react-intl';
import { useAuthMutation } from '@packages/shared/src/hooks/useAuthMutation/useAuthMutation';
import type { LineItemFragmentFragment } from '@packages/gql/generated/shopping/graphql';
import { type ProductPriceFragmentFragmentDoc } from '@packages/gql/generated/shopping/ProductPriceFragmentFragmentDoc';
import { unmask } from '@packages/gql/src/betterMasking';
import { AddToBasketDocument } from '@packages/gql/generated/shopping/AddToBasketDocument';
import { MessageFragmentFragmentDoc } from '@packages/gql/generated/shopping/MessageFragmentFragmentDoc';
import { ChangeProductDocument } from '@packages/gql/generated/shopping/ChangeProductDocument';
import { useNotification } from '@packages/shared/src/providers/NotificationProvider/useNotification';
import { selectedQuantity, selectedServices } from '../atoms';
import { getSkuPart } from '../helpers/getSkuPart/getSkuPart';
import type { UseAddToBasketTrackingProps } from '../Tracking/hooks/useAddToBasketTracking/useAddToBasketTracking';
import { useAddToBasketTracking } from '../Tracking/hooks/useAddToBasketTracking/useAddToBasketTracking';
import { addToBasketMessages } from './messages';
import { getProductFromLineItems } from './getProductFromLineItems';
import { getAplusDataForBasket } from '../APlus/helper/getAplusDataForBasket';

/* GraphQL */ `
  mutation AddToBasket(
    $locale: String!
    $sku: String!
    $count: Int!
    $isSpecialOrder: Boolean!
    $serviceLineitems: [AddBasketServiceLineitem!]
    $trackingParameter: BasketProductTrackingParameter
    $returnAllProducts: Boolean!
  ) {
    addToBasket(
      locale: $locale
      product: {
        sku: $sku
        count: $count
        isSpecialOrder: $isSpecialOrder
        basketOption: []
        serviceLineitems: $serviceLineitems
        trackingParameter: $trackingParameter
      }
      returnAllProducts: $returnAllProducts
    ) {
      itemCount
      success
      messages {
        ...MessageFragment
      }
      lineItems {
        product {
          sku
        }
        ...LineItemFragment
        ...TrackGlycerinAddToCartLineItem
      }
    }
  }
`;

/* GraphQL */ `
  mutation ChangeProduct(
    $locale: String!
    $sku: String!
    $lineItemId: String!
    $count: Int!
    $serviceLineitems: [AddBasketServiceLineitem!]
  ) {
    changeProduct(
      locale: $locale
      product: {
        sku: $sku
        lineItemId: $lineItemId
        count: $count
        serviceLineitems: $serviceLineitems
        basketOption: []
      }
    ) {
      lineItems {
        ...LineItemFragment
      }
      success
      messages {
        ...MessageFragment
      }
    }
  }
`;

export type UseBasketDataProps = UseAddToBasketTrackingProps & {
  lineItemId?: string;
  onClose?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  customButtonText?: MessageDescriptor | null;
};

export const useBasketData = ({
  trackingProductData,
  lineItemId,
  onClose,
  customButtonText,
  addToBasketTrackingAplus,
}: UseBasketDataProps) => {
  const {
    akl,
    sku,
    price: priceData,
    availability: { state, isOrderable },
  } = trackingProductData;

  const price = unmask<typeof ProductPriceFragmentFragmentDoc>(priceData);

  const [dialogOpened, setDialogOpened] = useState(false);

  const quantity = useAtomValue(selectedQuantity);
  const aPlusData = useAtomValue(selectedServices);

  const { formatMessage } = useIntl();
  const { jwtToken } = useSession();
  const { setDefaultProductOutcome } = useAbTesting();
  const { locale } = useI18n();
  const { addNotification } = useNotification();

  const isAvailable = state !== 'UNSELECTED' ? isOrderable : true;

  const [{ data: addToBasketMutationResult, fetching }, executeAddToBasket] =
    useAuthMutation(AddToBasketDocument);
  const [{ fetching: miniDVFetching }, executeChangeProduct] =
    useAuthMutation(ChangeProductDocument);

  const {
    trackLayerClosed,
    trackToBasketClick,
    trackToCheckAndOrder,
    trackAddToCart,
    trackCatalogAddedProduct,
    trackGlycerinAddToCart,
    getTrackingParameter,
    trackUpdateCart,
  } = useAddToBasketTracking({
    trackingProductData,
    addToBasketTrackingAplus,
  });

  const isLoading = !jwtToken || fetching || miniDVFetching;

  // It works only for miniDV to update product.
  const miniDVOnClickHandler = async (event: React.MouseEvent<HTMLButtonElement>) => {
    const result = await executeChangeProduct({
      locale,
      sku,
      lineItemId: lineItemId!,
      count: quantity,
      serviceLineitems: getAplusDataForBasket(aPlusData),
    });

    if (result.error || (result.data && !result.data.changeProduct.success)) {
      addNotification({
        message: formatMessage(addToBasketMessages.errorMessage),
        severity: 'error',
        duration: 5000,
      });
    } else if (result.data?.changeProduct) {
      addNotification({
        message: formatMessage(addToBasketMessages.miniDVSuccess),
        severity: 'success',
        duration: 5000,
      });
      getBridge().updateBasket();
      trackUpdateCart(result.data.changeProduct.lineItems, lineItemId);
      if (onClose && typeof onClose === 'function') {
        onClose(event);
      }
    }
  };

  // It works for both miniDV and detail view page to add product.
  const onClickHandler = async (event: React.MouseEvent<HTMLButtonElement>) => {
    if (state === 'UNSELECTED') {
      addNotification({
        message: formatMessage(addToBasketMessages.warningMessage),
        severity: 'warning',
        duration: 5000,
      });
    } else {
      const result = await executeAddToBasket({
        locale,
        sku,
        count: quantity,
        isSpecialOrder: false,
        serviceLineitems: getAplusDataForBasket(aPlusData),
        trackingParameter: getTrackingParameter(),
        returnAllProducts: true,
      });

      if (onClose && typeof onClose === 'function') {
        onClose(event);
      }

      if (
        result.error ||
        (result.data && !result.data.addToBasket) ||
        (!result.data?.addToBasket.success && result.data?.addToBasket.messages)
      ) {
        addNotification({
          message:
            result.data?.addToBasket.messages && result.data.addToBasket.messages.length > 0
              ? getFragmentData(MessageFragmentFragmentDoc, result.data.addToBasket.messages)[0]
                  .description
              : formatMessage(addToBasketMessages.errorMessage),
          severity: 'error',
          duration: 5000,
        });
        setDialogOpened(false);
      } else if (result.data?.addToBasket) {
        getBridge().updateBasket();
        startTransition(() => setDialogOpened(true));

        setDefaultProductOutcome(
          [
            {
              count: quantity,
              masterId: akl,
              sku: getSkuPart(sku, 'ArticleNumberAndSize'),
              value: price.value,
            },
          ],
          'ATB',
        );

        trackToCheckAndOrder();
        trackAddToCart({ lineItems: result.data.addToBasket.lineItems });
        trackGlycerinAddToCart({ lineItems: result.data.addToBasket.lineItems });
        trackCatalogAddedProduct();
      }
    }
  };

  const trackToCheckAndOrderHandler = () => {
    trackToBasketClick();
  };

  const layerCloseHandler = () => {
    startTransition(() => setDialogOpened(false));
    trackLayerClosed();
  };

  const handleButtonText = (): MessageDescriptor => {
    if (isAvailable && lineItemId) {
      return addToBasketMessages.miniDVButtonText;
    }
    if (isAvailable && lineItemId === undefined) {
      return customButtonText ?? addToBasketMessages.available;
    }
    return addToBasketMessages.soldOut;
  };

  const lineItemForDialog: LineItemFragmentFragment | undefined = useMemo(() => {
    if (
      addToBasketMutationResult?.addToBasket.success &&
      addToBasketMutationResult.addToBasket.lineItems.length > 0
    ) {
      return getProductFromLineItems(addToBasketMutationResult.addToBasket.lineItems, sku);
    }
    return undefined;
  }, [addToBasketMutationResult, sku]);

  return {
    isAvailable,
    isLoading,
    dialogOpened,
    miniDVOnClickHandler,
    onClickHandler,
    trackToCheckAndOrderHandler,
    layerCloseHandler,
    handleButtonText,
    lineItemForDialog,
  };
};
